<template>
  <form
    class="flex flex-col overflow-hidden"
    @submit="onSubmit"
  >
    <main class="space-y-3 overflow-y-auto p-4">
      <ProductModifiersFormField
        class="space-y-3"
        name="modifiers"
        :modifier-groups="modifierGroups"
        :modifier-group-ids-with-errors="
          modifierGroupIdsWithErrors
        "
      />

      <div class="relative flex flex-col">
        <div
          class="mb-1 block flex-1 text-xs font-medium text-background-fg/70"
        >
          Note
        </div>
        <div class="relative text-background-fg/50">
          <IconPencil
            class="absolute left-3 top-1/2 mt-[-8px] size-4"
          />
          <input
            v-model="note"
            placeholder="Add note..."
            tabindex="-1"
            class="size-full rounded-md border bg-background py-3 pl-9 pr-2 text-sm placeholder:text-background-fg/30 focus:border-primary/70 focus:outline-none"
          />
        </div>
      </div>
    </main>
    <footer
      class="space-y-3 border-t bg-background px-4 pb-4 pt-2"
    >
      <div>
        <div
          v-if="hasModifiers"
          class="flex items-center justify-between"
        >
          <div class="text-xs text-background-fg/70"
            >Base Price</div
          >
          <div
            class="text-sm font-semibold text-background-fg/50"
          >
            {{
              formatToMerchantCurrencyString(product.price)
            }}
          </div>
        </div>
        <div
          v-if="hasModifiers"
          class="flex items-center justify-between"
        >
          <div class="text-xs text-background-fg/70"
            >Modifiers</div
          >
          <div
            class="text-sm font-semibold text-background-fg/50"
            >{{
              formatToMerchantCurrencyString(
                totalPriceOffset
              )
            }}</div
          >
        </div>
        <div
          v-if="(values.quantity ?? 0) > 1"
          class="flex justify-between"
        >
          <div class="text-xs text-background-fg/70">
            Item Price
          </div>
          <div class="text-sm font-semibold">
            {{
              formatToMerchantCurrencyString(
                Math.max(
                  0,
                  totalPriceOffset + product.price
                )
              )
            }}
          </div>
        </div>
        <div class="flex justify-between">
          <div
            class="text-sm font-medium text-background-fg/70"
          >
            Total Price
          </div>
          <div class="text-xl font-semibold text-primary">
            {{
              formatToMerchantCurrencyString(
                Math.max(
                  0,
                  totalPriceOffset + product.price
                ) * (values.quantity ?? 0)
              )
            }}
          </div>
        </div>
      </div>

      <div
        class="flex"
        :class="[
          'flex',
          hasModifiers ? 'justify-end' : 'justify-between',
        ]"
      >
        <div class="flex w-24 items-center justify-between">
          <button
            class="flex items-center justify-center rounded-full border border-background-fg/20 p-1"
            type="button"
            :disabled="values.quantity === 1 && !isEditing"
            tabindex="-1"
            @click="decrementQuantity"
          >
            <template
              v-if="values.quantity === 1 && isEditing"
            >
              <IconTrash class="m-[2px]" />
            </template>
            <template v-else>
              <IconMinus class="m-[2px]" />
            </template>
          </button>
          <p
            class="line-clamp-1 text-right text-sm font-semibold"
          >
            {{ values.quantity }}
          </p>
          <button
            class="flex items-center justify-center rounded-full border border-background-fg/20 p-1"
            type="button"
            tabindex="-1"
            @click="incrementQuantity"
          >
            <IconPlus class="m-[2px]" />
          </button>
        </div>

        <ActionButtonPrimary type="submit" class="ml-2">
          <template v-if="isEditing">
            Update Item
          </template>
          <template v-else> Add to cart </template>
        </ActionButtonPrimary>
      </div>
    </footer>
  </form>

  <BaseConfirmation
    ref="deleteConfirmDialogRef"
    description="Are you sure you wish to delete this item?"
    confirm-label="Delete Item"
    cancel-label="No"
  />
</template>

<script setup lang="ts">
import type { Product } from "@/services/sharedTypes/products"
import {
  useOrderItemForm,
  type OrderItemFormInput,
  type OrderItemFormOutput,
} from "@/forms/orderItemForm"
import { useProductModifers } from "@/services/product-modifiers/listProductModifiers"

const { formatToMerchantCurrencyString } = useCurrency()

const props = withDefaults(
  defineProps<{
    product: Product
    isEditing?: boolean
    initialValues?: OrderItemFormInput
  }>(),
  {
    initialValues: () => ({
      note: "",
      quantity: 1,
      modifiers: [],
    }),
  }
)
const emit = defineEmits<{
  (event: "submit", params: OrderItemFormOutput): void
  (event: "delete"): void
}>()

const { values, setFieldValue, handleSubmit, defineField } =
  useOrderItemForm({
    ...props.initialValues,
    modifiers: props.initialValues.modifiers?.map((m) => ({
      ...m,
    })),
  })

const [note] = defineField("note")

const onSubmit = handleSubmit((formValues) => {
  modifierGroupIdsWithErrors.value = modifierGroups.value
    .filter((group) => {
      const modifierOptionIdsForGroup =
        formValues.modifiers.filter(
          (option) => option.groupId === group.id
        )

      return (
        modifierOptionIdsForGroup.length <
          group.minOptions ||
        modifierOptionIdsForGroup.length > group.maxOptions
      )
    })
    .map((group) => group.id)

  if (modifierGroupIdsWithErrors.value.length > 0) {
    return
  }

  emit("submit", formValues)
})

const { getByIds } = useProductModifers()
const modifierGroups = computed(
  () => getByIds(props.product.modifierGroupIds ?? []).value
)
const modifierGroupIdsWithErrors = ref<Array<string>>([])
const hasModifiers = computed(
  () => props.product.modifierGroupIds.length > 0
)

const totalPriceOffset = computed(
  () =>
    values.modifiers?.reduce(
      (acc, option) => acc + option.priceOffset,
      0
    ) ?? 0
)

const deleteConfirmDialogRef = useTemplateRef(
  "deleteConfirmDialogRef"
)
const incrementQuantity = () =>
  setFieldValue("quantity", (values.quantity ?? 0) + 1)
const decrementQuantity = async () => {
  if ((values.quantity ?? 0) > 1) {
    setFieldValue("quantity", (values.quantity ?? 0) - 1)
  } else {
    if (deleteConfirmDialogRef.value === null) return
    const { isCanceled: isDeleteCancelled } =
      await deleteConfirmDialogRef.value.reveal()
    if (isDeleteCancelled) return

    emit("delete")
  }
}
</script>

<style module lang="scss">
.noteInputeTextArea:placeholder-shown {
  & + .noteInputPlaceholder {
    visibility: visible;
  }

  &:focus + .noteInputPlaceholder {
    visibility: hidden;
  }
}
</style>
